import { Mic2, Square } from 'lucide-solid';
import { createEffect, createMemo, createSignal, For, onCleanup, Show, untrack, type JSX } from 'solid-js';
import { BusyButton, BusySlot, EffectUnavailable, InlineNotice, Metric } from '../components/ui';
import { audioTelemetryFromStatus, type AudioTelemetryFeed } from '../lib/audioTelemetry';
import {
  EFFECT_STACK_MIXES,
  parseEffectStack,
  shownEffectStack,
  type EffectStack,
  type EffectStackMix,
  type EffectStackPatch,
} from '../lib/effectStack';
import type { EffectPreset } from '../lib/effectPresets';
import {
  AUDIO_REACTIVE_PACKAGE_ID,
  parameterLabel,
  sameEffectLibrary,
  type EffectPackageItem,
  type EffectPackageParameterValue,
} from '../lib/effectPackages';
import {
  EFFECT_MAP_NOT_LOADED,
  effectLayoutZoneEnds,
  effectMapIsStale,
  isEffectMapRigMismatchError,
} from '../lib/effectLayout';
import { audioReactiveStartParams } from '../lib/playLook';
import { plainError, reportError } from '../lib/plainErrors';
import { silenceFallbackControls } from '../lib/silenceFallback';
import type { SettingsTreeSource } from '../lib/settingsTree';
import { useVenue } from '../state/VenueProvider';
import {
  sendSettingAwaited,
  sendSettingGesture,
  stackPatchWrites,
} from '../state/effectSettings';
import {
  AudioStackAdd,
  audioStackPlus,
  createAudioStackNodes,
  layerPrefix,
  stackLayerIds,
  type AudioStackModel,
} from './AudioStackEditor';
import { EffectPresets } from './EffectPresets';
import { openSettingsTreeOn, SettingsTreeRootRow, SettingsTreeSheet } from './SettingsTreeSheet';
import type { TreeCardProps } from './treeCards';
import { TEST_IDS, testIdFor, testIdOf } from '../testIds';

/** Audio reactive keeps only effect controls here. Input selection and live analysis belong to Music. */

const STATUS_FALLBACK = 'The controller stopped reporting on the audio engine.';

/**
 * The daemon's own sentence when it supplied one; otherwise the console's existing plain fallback. A
 * refusal of the effect map itself names layouts and digests, so it is said in the console's own words.
 */
function effectFailureMessage(error: unknown): string {
  if (isEffectMapRigMismatchError(error) || EFFECT_MAP_NOT_LOADED.test(plainError(error).detail)) {
    return plainError(error).message;
  }
  if (typeof error === 'string' && error.trim()) return error.trim();
  const message = (error as { message?: unknown } | null | undefined)?.message;
  if (typeof message === 'string' && message.trim()) return message.trim();
  return plainError(error).message;
}

interface AudioParameters {
  preset?: string;
  reactivityAmount?: number;
  brightnessCeiling?: number;
  quietFloor?: number;
  responseSpeed?: number;
  zoneBalance?: number;
  accentAmount?: number;
  silenceSpeed?: number;
  silenceLevel?: number;
  /** The operator's stack — how it combines, and the layers in it — exactly as the controller holds it. */
  stack?: { mix?: unknown; layers?: unknown };
}

interface AudioReactiveStatus {
  effectId?: string;
  preset?: string;
  state?: string;
  layoutDigest?: string;
  elementCount?: number;
  sequence?: number;
  requestedPatchSequence?: number;
  frameAgeMs?: number | null;
  staleAfterMs?: number;
  staleReleaseMs?: number;
  /** 1.7.6 reports the configured sidecar and wire rates beside the live status. */
  fps?: number;
  outputHz?: number;
  /** What the stack is actually rendering at, beside the rate it is asked for. */
  outputRate?: { hz?: number; targetHz?: number; belowTarget?: boolean };
  socket?: { connected?: boolean; ready?: boolean; reconnectCount?: number; lastError?: string };
  sidecar?: { mode?: string; running?: boolean; pid?: number; error?: string };
  telemetry?: AudioTelemetryFeed;
  renderRateWarning?: string;
}

type MacroKey = Exclude<keyof AudioParameters, 'preset' | 'stack'>;

/**
 * Every macro the engine exposes, with the range the daemon validates it against
 * (`internal/live/effect/audio_reactive.go`). `zoneBalance`'s meaning is filled in from the room's own
 * zones — a general console must not name one venue's furniture.
 */
const ENGINE_PARAMETERS: Array<{ key: MacroKey; meaning: string; min: number; max: number; step?: number; default?: number; label?: string }> = [
  { key: 'reactivityAmount', meaning: 'How closely the room follows the music', min: 0, max: 1 },
  { key: 'brightnessCeiling', meaning: 'The brightest the room may get', min: 0, max: 1 },
  { key: 'quietFloor', meaning: 'Light held when nothing is playing', min: 0, max: 1 },
  { key: 'responseSpeed', meaning: 'How fast the room reacts, from slow to immediate', min: 0, max: 1 },
  { key: 'zoneBalance', meaning: '', min: -1, max: 1 },
  { key: 'accentAmount', meaning: 'Extra emphasis on beats and transients', min: 0, max: 1 },
];

/** The owner's own two words for the two ways a stack combines. */
const MIX_LABELS: Record<EffectStackMix, string> = {
  max: 'Brightest wins',
  add: 'Add',
};

function titleCase(value: string): string {
  return value.replace(/[._-]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

/** A parameter readout. An em dash means the daemon has not reported the value, never "zero". */
function decimal(value: number | undefined): string {
  return Number.isFinite(value) ? (value as number).toFixed(2) : '—';
}

/**
 * How long the audio-reactive effect has to be MISSING before this card believes it is gone.
 *
 * The room's state reaches a cloud console as a mirror frame a few times a second, and a single frame
 * that arrives without the effect block is not news that the effect stopped — it is one frame. A second
 * and a half is several frames' worth of silence: long enough that a real stop is answered promptly,
 * short enough that a stop the operator did not make does not sit on screen.
 */
const EFFECT_ABSENCE_GRACE_MS = 1_500;

/**
 * The Audio reactive card: a tree-shaped card whose tree is its layer stack (S34).
 *
 * The pane holds what belongs to the whole effect — Start / Stop, Mix and the six macros, the frame
 * rate, the named looks — and the stack's first level as a list. Choosing a layer opens the tree sheet
 * on it (B84), where its package, sync, driver and own parameters are edited.
 */
export function AudioReactivePanel(props: TreeCardProps): JSX.Element {
  const venue = useVenue();
  const startState = props.startState;
  if (!startState) throw new Error('Audio reactive start state is missing');
  const [statusReadProblem, setStatusReadProblem] = createSignal('');
  const [actionProblem, setActionProblem] = createSignal('');
  const statusProblem = createMemo(() => actionProblem() || statusReadProblem());
  const [queriedStatus, setQueriedStatus] = createSignal<AudioReactiveStatus>();
  const [sheetOpen, setSheetOpen] = createSignal(false);
  const view = createMemo(() => venue.settingsTreeView.view(
    `${venue.selectedVenue()?.venueId ?? ''}:${venue.selectedRoom()?.roomId ?? ''}:${AUDIO_REACTIVE_PACKAGE_ID}`,
  ));

  let statusInFlight = false;
  let queuedStatusId = '';
  let statusReadKey = '';
  let loggedStatusFailure = '';
  let loggedPatchError = '';
  let absenceTimer: ReturnType<typeof globalThis.setTimeout> | undefined;

  async function callEffectController<T = unknown>(name: string, params: Record<string, unknown>): Promise<T> {
    try {
      return await venue.controllerCall<T>(name, params);
    } catch (error) {
      // Start, Stop, recall and direct parameter writes share the card's existing problem line. The
      // provider still owns the global command feedback; this is the durable account beside the control.
      setActionProblem(effectFailureMessage(error));
      throw error;
    }
  }

  const layout = () => props.layout;
  const selectedInput = startState.input;
  const startReadiness = startState.readiness;

  /**
   * Everything one stack layer may be pointed at: the installed packages and the room's reactions. It
   * holds its PREVIOUS array whenever the library still says the same thing — the mirror re-delivers the
   * whole controller node several times a second, and a new array would rebuild every picker.
   */
  const layerCatalog = createMemo<EffectPackageItem[]>((previous) => {
    const next = props.catalog();
    return previous && sameEffectLibrary(previous, next) ? previous : next;
  }, []);
  const engineParameters = createMemo(() => [
    ...ENGINE_PARAMETERS,
    ...silenceFallbackControls(props.catalog().find((item) => item.packageId === AUDIO_REACTIVE_PACKAGE_ID))
      .map(({ key, label, spec }) => ({ key, label, meaning: spec.help ?? '',
        min: spec.min, max: spec.max, step: spec.step, default: spec.default })),
  ]);

  const { effectId, reported: roomSettings, hold: settingsHold } = startState.settings;
  const shownStack = createMemo(() => parseEffectStack({
    mix: settingsHold.shown().mix, layers: settingsHold.shown().layers,
  }));
  const reportedStack = createMemo(() => parseEffectStack({
    mix: roomSettings().mix, layers: roomSettings().layers,
  }));

  /**
   * On LAN and Bluetooth the daemon PUSHES `audio_reactive_status`, which remains the whole feed. On the
   * cloud origin one bounded query supplies the status at open/start. The generated mirror has no live
   * `effect.audio` member, so this card neither invents one nor grows a query loop around its absence.
   */
  const directFeed = createMemo(() => venue.controlTransport() === 'lan' || venue.controlTransport() === 'bluetooth');
  const status = createMemo<AudioReactiveStatus | undefined>(() => {
    const id = effectId();
    if (!id) return undefined;
    const value = (directFeed() ? venue.audioStatus() : queriedStatus()) as AudioReactiveStatus | undefined;
    return value?.effectId === id ? value : undefined;
  });
  const statusTelemetry = createMemo(() => audioTelemetryFromStatus(status()));
  /**
   * What the stack is rendering at, as the controller measures it. A rate the daemon has not reported
   * yet is nothing to show — the card says nothing rather than reading zero back as a fact.
   */
  const outputRate = createMemo(() => {
    const reported = status()?.outputRate;
    const hz = Number(reported?.hz);
    if (!reported || !Number.isFinite(hz) || hz <= 0) return undefined;
    return { hz, belowTarget: reported.belowTarget === true };
  });

  const zoneEnds = createMemo(() => {
    const loaded = layout()?.layout;
    if (loaded) {
      const names = effectLayoutZoneEnds(loaded);
      if (names.length >= 2) return [titleCase(names[0]), titleCase(names[names.length - 1])];
    }
    const zones = venue.roomModel()?.zones ?? [];
    if (zones.length >= 2) return [zones[0].name, zones[zones.length - 1].name];
    return [];
  });
  const audioZoneNames = createMemo(() => new Map(
    (venue.roomModel()?.zones ?? []).map((zone) => [zone.id, zone.name]),
  ));
  const zoneBalanceMeaning = createMemo(() => {
    const ends = zoneEnds();
    return ends.length === 2 ? `${ends[0]} ↔ ${ends[1]}` : 'Toward one end of the room ↔ the other';
  });

  async function readAudioStatus(id: string): Promise<void> {
    if (statusInFlight) {
      queuedStatusId = id;
      return;
    }
    statusInFlight = true;
    try {
      // The provider guards this read and files its result into audioStatus for every screen.
      const result = await venue.controllerRead<AudioReactiveStatus>('get_audio_reactive_status', { effectId: id });
      if (!directFeed() && result?.effectId === id) setQueriedStatus(result);
      setStatusReadProblem('');
      loggedStatusFailure = '';
    } catch (error) {
      const plain = plainError(error, STATUS_FALLBACK);
      if (plain.detail !== loggedStatusFailure) {
        loggedStatusFailure = plain.detail;
        reportError('audio reactive status', error, STATUS_FALLBACK);
      }
      setStatusReadProblem(plain.message);
    } finally {
      statusInFlight = false;
      const queued = queuedStatusId;
      queuedStatusId = '';
      if (queued && queued === effectId()) void readAudioStatus(queued);
    }
  }

  createEffect(() => {
    const id = effectId();
    if (absenceTimer != null) {
      globalThis.clearTimeout(absenceTimer);
      absenceTimer = undefined;
    }
    if (!id) {
      absenceTimer = globalThis.setTimeout(() => {
        absenceTimer = undefined;
        if (effectId()) return;
        statusReadKey = '';
        queuedStatusId = '';
        setQueriedStatus(undefined);
        setStatusReadProblem('');
      }, EFFECT_ABSENCE_GRACE_MS);
      return;
    }
    const key = `${directFeed() ? 'direct' : 'cloud'}:${id}`;
    if (statusReadKey === key) return;
    statusReadKey = key;
    void readAudioStatus(id);
  });

  onCleanup(() => {
    if (absenceTimer != null) globalThis.clearTimeout(absenceTimer);
  });

  createEffect(() => {
    const raw = statusTelemetry()?.patchError;
    if (!raw || raw === loggedPatchError) return;
    loggedPatchError = raw;
    reportError('audio parameter patch', new Error(raw), 'The engine rejected the last parameter change.');
  });

  function macroValue(key: MacroKey): number | undefined {
    const value = settingsHold.shown().parameters[key];
    return typeof value === 'number' && Number.isFinite(value)
      ? value : engineParameters().find((item) => item.key === key)?.default;
  }

  function updateMacro(key: MacroKey, value: number, final: boolean): number {
    const definition = engineParameters().find((item) => item.key === key)!;
    let capped = Math.max(definition.min, Math.min(definition.max, value));
    if (key === 'quietFloor') capped = Math.min(capped, macroValue('brightnessCeiling') ?? 1);
    if (key === 'brightnessCeiling') capped = Math.max(capped, macroValue('quietFloor') ?? 0);
    setActionProblem('');
    sendSettingGesture(venue, AUDIO_REACTIVE_PACKAGE_ID,
      [{ path: `parameters/${key}`, value: capped }], final, settingsHold,
      (error) => setActionProblem(effectFailureMessage(error)),
      (error) => setActionProblem(plainError(error, STATUS_FALLBACK).message));
    return capped;
  }

  function updateStack(patch: EffectStackPatch, _key: string, final: boolean, preserveProblem = false): void {
    if (!preserveProblem) setActionProblem('');
    const writes = stackPatchWrites(patch, shownStack());
    if ('replace' in patch) settingsHold.clearReplacedLayers();
    sendSettingGesture(venue, AUDIO_REACTIVE_PACKAGE_ID, writes, final, settingsHold,
      (error) => setActionProblem(effectFailureMessage(error)),
      (error) => setActionProblem(plainError(error, STATUS_FALLBACK).message));
  }

  async function applyConductor(macros: Partial<Record<MacroKey, number>>): Promise<boolean> {
    setActionProblem('');
    const writes = Object.entries(macros).map(([key, value]) => ({ path: `parameters/${key}`, value }));
    if (!writes.length) return true;
    const applied = await venue.runAction('effect-preset', async () => {
      await sendSettingAwaited(callEffectController, AUDIO_REACTIVE_PACKAGE_ID, writes, settingsHold, venue);
      return true;
    }, 'Preset applied');
    return Boolean(applied);
  }

  /** Only values the current room declares can be restored; absent values remain untouched. */
  function recalledMacros(values: Record<string, EffectPackageParameterValue>): Partial<Record<MacroKey, number>> {
    const macros: Partial<Record<MacroKey, number>> = {};
    for (const definition of engineParameters()) {
      const value = values[definition.key];
      if (typeof value !== 'number' || !Number.isFinite(value)) continue;
      macros[definition.key] = value;
    }
    return macros;
  }

  /** Save every declared room control as shown, including the optional silence controls. */
  function savedMacros(): Record<string, EffectPackageParameterValue> {
    const values: Record<string, EffectPackageParameterValue> = {};
    for (const definition of engineParameters()) {
      const value = macroValue(definition.key);
      if (value != null) values[definition.key] = value;
    }
    return values;
  }

  function recalledStack(preset: EffectPreset): EffectStack | undefined {
    return preset.stack;
  }

  /**
   * Put a saved look back: the conductor in one patch, then the whole stack in one more. TWO writes,
   * never more — the conductor first, because it is the half that RESETS. A layer whose package this
   * room does not have is left out and said so, rather than taking the whole recall down with it.
   */
  async function recallAudioPreset(preset: EffectPreset): Promise<void> {
    if (!await applyConductor(recalledMacros(preset.values))) return;
    const saved = recalledStack(preset);
    if (!saved) return;
    const runnable = saved.layers.filter((layer) => (
      layerCatalog().some((item) => item.packageId === layer.effectId && item.available)
    ));
    const missing = saved.layers.filter((layer) => !runnable.includes(layer));
    if (missing.length) {
      const names = missing.map((layer) => (
        layerCatalog().find((item) => item.packageId === layer.effectId)?.name ?? layer.effectId
      ));
      setActionProblem(names.length === 1
        ? `This room no longer has ${names[0]}, so that layer was left out.`
        : `This room no longer has ${names.join(', ')}, so those layers were left out.`);
    } else {
      setActionProblem('');
    }
    // Nothing of the stack survived, so there is no stack to put back.
    if (!runnable.length) return;
    updateStack({ replace: { mix: saved.mix, layers: runnable } }, 'replace', true, missing.length > 0);
  }

  async function start(): Promise<void> {
    if (!startReadiness().enabled) return;
    const loaded = layout()!;
    const input = selectedInput()!;
    // Only a direct link (LAN/Bluetooth) carries the controller's own rig, so only it can be pre-checked here.
    // Over the cloud `get_rig` is not relayable; `start_audio_reactive` itself is the staleness check — the
    // controller refuses a map that is not its loaded layout, and the catch below reloads the room rig (S38, H42).
    if (directFeed() && effectMapIsStale(loaded.layout.layoutDigest, venue.rig()?.effectLayout?.layoutDigest)) {
      // The reload announces itself — "Room updated" is said once, by the notice at the top.
      await venue.reloadRoomRig().catch((error) => reportError('room reload', error));
      return;
    }
    if (!await venue.confirmTakeover({
      source: 'effect',
      action: 'starts Audio reactive',
    })) return;
    setActionProblem('');
    await venue.runAction('start-effect', async () => {
      try {
        return await callEffectController('start_audio_reactive', {
          // The same start Play sends for a look (`lib/playLook.ts`), so the two can never drift apart.
          ...audioReactiveStartParams(loaded.layout, { ...input, identity: input.identity ?? '' }, venue.roomLayout()),
        });
      } catch (error) {
        if (isEffectMapRigMismatchError(error)) {
          // The reload's "Room updated" notice is the whole account; the card repeats nothing.
          setActionProblem('');
          await venue.reloadRoomRig().catch((reloadError) => reportError('room reload', reloadError));
        }
        throw error;
      }
    }, 'Audio reactive started');
  }

  // A double-click on this card is its Start, pressed.
  createEffect(() => {
    if (!props.startRequested()) return;
    props.onStartHandled();
    untrack(() => void start());
  });

  async function stop(): Promise<void> {
    const id = effectId();
    if (!id) return;
    setActionProblem('');
    const stopped = await venue.runAction(
      'stop-effect',
      async () => {
        await callEffectController('stop_effect', { effectId: id });
        return true;
      },
      'Effect stopped',
    );
    if (!stopped) return;
    setQueriedStatus(undefined);
  }

  // ---- The stack as a tree: the one adapter for the audio stack model -------------------------------

  const stackModel: AudioStackModel = {
    effectId,
    items: layerCatalog,
    layers: () => shownStack().layers,
    reported: () => reportedStack().layers,
    zoneNames: audioZoneNames,
    inFlight: (gestureKey) => {
      if (gestureKey === 'baseline') {
        const baseline = reportedStack().layers.find((layer) => layer.zone === 'baseline')
          ?? shownStack().layers.find((layer) => layer.zone === 'baseline');
        return baseline ? settingsHold.committing(`layers/${baseline.id}`)
          || settingsHold.committing(`layers/${baseline.id}/effectId`) : false;
      }
      const id = gestureKey.split(':')[1];
      return Boolean(id && settingsHold.committing(`layers/${id}/effectId`));
    },
    disabled: () => !venue.controlsEnabled(),
    committing: (path) => settingsHold.committing(path),
    onPatch: updateStack,
    afterRemove: (control) => control.select(''),
  };
  const layerNode = createAudioStackNodes(stackModel);
  const layerIds = createMemo(() => stackLayerIds(shownStack().layers), [], {
    equals: (left, right) => left.length === right.length && left.every((id, index) => id === right[index]),
  });
  // The card's own Add is the tree's plus: its layers are the first level, so no row adds beneath itself.
  const plus = audioStackPlus(stackModel);
  const source: SettingsTreeSource = { roots: layerIds, node: layerNode, plus };

  function openLayer(id: string): void {
    openSettingsTreeOn(view(), id);
    setSheetOpen(true);
  }

  return (
    <section class="surface effect-stage audio-effect-stage" id="audio-reactive-panel">
      <div class="section-heading">
        <div>
          <h2>Audio reactive</h2>
        </div>
      </div>

      <Show when={!selectedInput()}>
        <InlineNotice tone="warning">
          <span data-testid={TEST_IDS.musicInputNeeded}>Choose a music input on the <a href="#/music">Music tab</a>.</span>
        </InlineNotice>
      </Show>

      <section class="audio-stack" data-testid={TEST_IDS.audioStack}>
        <ul class="settings-root-list" data-testid={TEST_IDS.audioStackLayers}>
          <For each={layerIds()}>
            {(id) => (
              <li>
                <Show when={layerNode(id)}>
                  {(node) => <SettingsTreeRootRow node={node()} testId={layerPrefix(id)} onOpen={() => openLayer(id)} />}
                </Show>
              </li>
            )}
          </For>
        </ul>
        <div class="audio-stack-add-row">
          <AudioStackAdd model={stackModel} />
        </div>
      </section>

      <div class="engine-parameters" data-testid={TEST_IDS.effectParameters}>
        <div class="engine-parameters-head">
          <strong>Parameters</strong>
        </div>
        <div class="audio-stack-field">
          <span>Mix</span>
          <div class="effect-package-segmented" role="radiogroup" aria-label="Mix">
            <For each={EFFECT_STACK_MIXES}>
              {(mix) => (
                <button
                  type="button"
                  role="radio"
                  classList={{ selected: shownStack().mix === mix }}
                  aria-checked={shownStack().mix === mix}
                  disabled={!venue.controlsEnabled()}
                  data-testid={testIdOf('audioStackMix', { mix })}
                  onClick={() => updateStack({ mix }, 'mix', true)}
                >{MIX_LABELS[mix]}</button>
              )}
            </For>
          </div>
        </div>
        <For each={engineParameters()}>
          {(definition) => {
            const label = definition.label ?? parameterLabel(definition.key);
            const value = () => macroValue(definition.key);
            const committing = () => settingsHold.committing(`parameters/${definition.key}`);
            return (
              <label class="engine-parameter">
                <span class="engine-parameter-name">
                  <strong>{label}</strong>
                  <small>
                    {definition.key === 'zoneBalance' ? zoneBalanceMeaning() : definition.meaning}
                  </small>
                </span>
                <output><BusySlot busy={committing()} />{decimal(value())}</output>
                <input
                  type="range"
                  min={definition.min}
                  max={definition.max}
                  step={definition.step ?? 0.01}
                  value={value() ?? definition.min}
                  disabled={!venue.controlsEnabled() || value() == null}
                  aria-label={label}
                  onInput={(event) => { event.currentTarget.value = String(updateMacro(definition.key, event.currentTarget.valueAsNumber, false)); }}
                  onChange={(event) => { event.currentTarget.value = String(updateMacro(definition.key, event.currentTarget.valueAsNumber, true)); }}
                />
              </label>
            );
          }}
        </For>
      </div>

      {/*
        * Always here, always the same size. The rate is a fact about a running room, so it is blank
        * before the daemon has measured one. The slot's height is fixed in CSS so Start, Stop and every
        * changing digit leave everything below it exactly where it was.
        */}
      <div class="audio-stack-rate">
        <Metric
          label="Frame rate"
          value={<span data-testid={TEST_IDS.audioFrameRate}>{outputRate() ? `${outputRate()!.hz.toFixed(1)} Hz` : ''}</span>}
          tone={outputRate()?.belowTarget ? 'warn' : undefined}
        />
      </div>

      {/*
        * The same library every other effect has, saving what this card is showing: the six macros and
        * the whole layer stack — and a recall puts all of it back.
        */}
      <EffectPresets
        packageId={AUDIO_REACTIVE_PACKAGE_ID}
        idPrefix={testIdFor.packagePrefix(AUDIO_REACTIVE_PACKAGE_ID)}
        values={savedMacros}
        stack={() => shownEffectStack(shownStack())}
        onApply={(preset) => void recallAudioPreset(preset)}
      />

      <Show when={statusProblem()}>
        <p class="form-error" data-testid={TEST_IDS.effectStatusProblem}>{statusProblem()}</p>
      </Show>

      <Show
        when={layout() || props.loading}
        fallback={(
          <EffectUnavailable
            effectName="Audio reactive"
            detail={props.unavailableDetail}
            testId={TEST_IDS.effectLayoutMissing}
          />
        )}
      >
        <Show
          when={effectId()}
          fallback={
            <BusyButton
              busyKey="start-effect"
              class="button button-primary button-large"
              disabled={!startReadiness().enabled}
              onClick={() => void start()}
              data-testid={TEST_IDS.startEffect}
            ><Mic2 size={18} /> Start</BusyButton>
          }
        >
          <BusyButton
            busyKey="stop-effect"
            class="button button-danger button-large"
            disabled={!venue.controlsEnabled()}
            onClick={() => void stop()}
            data-testid={TEST_IDS.stopEffect}
          ><Square size={17} /> Stop</BusyButton>
        </Show>
      </Show>

      <SettingsTreeSheet
        title="Audio reactive"
        source={source}
        view={view()}
        open={sheetOpen()}
        onClose={() => setSheetOpen(false)}
      />
    </section>
  );
}
